import React, { createContext, useContext, useState, useEffect } from "react";
import { authAPI } from "../lib/api";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // Initialize user from localStorage to prevent flash
  const [user, setUser] = useState(() => {
    try {
      const savedUser = localStorage.getItem("user");
      return savedUser ? JSON.parse(savedUser) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem("token");
      const savedUser = localStorage.getItem("user");

      if (token && savedUser) {
        try {
          // Verify token is still valid
          const response = await authAPI.me();
          setUser(response.data);
          localStorage.setItem("user", JSON.stringify(response.data));
        } catch (err) {
          // Token invalid, clear storage
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          setUser(null);
        }
      } else {
        // No token or user, ensure state is cleared
        setUser(null);
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (employeeId, password) => {
    setError(null);
    try {
      const response = await authAPI.login({ employee_id: employeeId, password });
      const { access_token, user: userData } = response.data;
      localStorage.setItem("token", access_token);
      localStorage.setItem("user", JSON.stringify(userData));
      setUser(userData);
      return userData;
    } catch (err) {
      const message = err.response?.data?.detail || "Login failed";
      setError(message);
      throw new Error(message);
    }
  };

  const register = async (data) => {
    setError(null);
    try {
      const response = await authAPI.register(data);
      const { access_token, user: userData } = response.data;
      localStorage.setItem("token", access_token);
      localStorage.setItem("user", JSON.stringify(userData));
      setUser(userData);
      return userData;
    } catch (err) {
      const message = err.response?.data?.detail || "Registration failed";
      setError(message);
      throw new Error(message);
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
  };

  const loginWithToken = async (token) => {
    setError(null);
    try {
      // Store the token first
      localStorage.setItem("token", token);
      
      // Verify and get user data
      const response = await authAPI.me();
      const userData = response.data;
      
      localStorage.setItem("user", JSON.stringify(userData));
      setUser(userData);
      return true;
    } catch (err) {
      // Token invalid, clear storage
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      setUser(null);
      const message = err.response?.data?.detail || "Invalid token";
      setError(message);
      return false;
    }
  };

  const isAdmin = () => user?.role === "admin";
  const isSupervisor = () => user?.role === "supervisor" || user?.role === "admin";
  const isWorker = () => user?.role === "worker" || user?.role === "operator";

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        login,
        loginWithToken,
        register,
        logout,
        isAdmin,
        isSupervisor,
        isWorker,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
